import React, { useState, useEffect } from "react";
import styled from 'styled-components';
import { Modal, Button } from "react-bootstrap";
import AddIcon from "@mui/icons-material/Add";
import Select from "react-dropdown-select";

import { fetchPrivacyTypeAPI, createNewPlaylistAPI } from "../../controller/userController";

const FloatingButton = styled.button`
    position: fixed;
    bottom: 30px;
    right: 30px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    box-shadow: 0 4px 16px rgba(15, 118, 110, 0.4);
    transition: all 0.3s ease;
    z-index: 1000;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 24px rgba(15, 118, 110, 0.6);
    }

    &:active {
        transform: scale(0.95);
    }
`;

const FormGroup = styled.div`
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FormLabel = styled.label`
    font-weight: 600;
    color: #1a1a1a;
    font-size: 14px;
`;

const FormInput = styled.input`
    padding: 12px;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #06b6d4;
        box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
    }

    &::placeholder {
        color: #999;
    }
`;

const FormSelect = styled.select`
    padding: 12px;
    border: 1px solid #d0d0d0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #1a1a1a;

    &:focus {
        outline: none;
        border-color: #06b6d4;
        box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
`;

const SaveButton = styled(Button)`
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%) !important;
    border: none !important;
    font-weight: 600;
    padding: 10px 20px !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(15, 118, 110, 0.3) !important;
    }
`;

const CancelButton = styled(Button)`
    background: #f0f0f0 !important;
    color: #1a1a1a !important;
    border: none !important;
    font-weight: 600;
    padding: 10px 20px !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;

    &:hover {
        background: #e0e0e0 !important;
    }
`;

const StyledModal = styled(Modal)`
    .modal-content {
        border-radius: 12px;
        border: none;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    }

    .modal-header {
        border-bottom: 1px solid #f0f0f0;
        padding: 20px;

        h5 {
            font-weight: 700;
            color: #1a1a1a;
        }
    }

    .modal-body {
        padding: 24px;
    }

    .modal-footer {
        border-top: 1px solid #f0f0f0;
        padding: 16px 20px;
        background: #fafafa;
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
    }

    .btn-close {
        filter: brightness(0.5);

        &:hover {
            filter: brightness(0.7);
        }
    }
`;

const ModalButton = styled.button`
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(15, 118, 110, 0.3);
    }

    &:active {
        transform: scale(0.95);
    }
`;

export default function NewPlaylist(props) {
    const [show, setShow] = useState(false);
    const [privacy, setPrivacy] = useState([]);
    const [playlistInfo, setPlaylistInfo] = useState({
        title: "",
        tags: [],
        privacy: 1,
    });

    const createPlaylistCallback = (data) => {
        props.refresh();
    };

    useEffect(() => {
        fetchPrivacyTypeAPI(setPrivacy);
        // eslint-disable-next-line
    }, []);

    const handleCancel = () => {
        setShow(false);
    };

    const handleSave = () => {
        createNewPlaylistAPI(playlistInfo, createPlaylistCallback);
        setPlaylistInfo({ title: "", tags: [], privacy: 1 });
        setShow(false);
    };

    const handleShow = () => {
        setShow(true);
    };

    return (
        <>
            {props.buttonType == "fixedButton" ? (
                <FloatingButton onClick={handleShow} title="Create new playlist">
                    <AddIcon sx={{ fontSize: '28px' }} />
                </FloatingButton>
            ) : null}

            {props.buttonType == "modalButton" ? (
                <ModalButton onClick={handleShow}>
                    Create Playlist
                </ModalButton>
            ) : null}

            <StyledModal size="md" show={show} onHide={handleCancel} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create New Playlist</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FormGroup>
                        <FormLabel>Playlist Title</FormLabel>
                        <FormInput
                            type="text"
                            placeholder="Enter playlist name"
                            value={playlistInfo.title}
                            onChange={(e) =>
                                setPlaylistInfo({
                                    ...playlistInfo,
                                    title: e.target.value,
                                })
                            }
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>Privacy</FormLabel>
                        <FormSelect
                            value={playlistInfo.privacy}
                            onChange={(e) =>
                                setPlaylistInfo({
                                    ...playlistInfo,
                                    privacy: parseInt(e.target.value),
                                })
                            }
                        >
                            {privacy.map((pv) => (
                                <option key={pv.privacy_id} value={pv.privacy_id}>
                                    {pv.privacy_name}
                                </option>
                            ))}
                        </FormSelect>
                    </FormGroup>
                </Modal.Body>

                <Modal.Footer>
                    <CancelButton onClick={handleCancel}>
                        Cancel
                    </CancelButton>
                    <SaveButton onClick={handleSave}>
                        Create
                    </SaveButton>
                </Modal.Footer>
            </StyledModal>
        </>
    );
}
